import React, { useCallback, useMemo, useState } from "react";
import { AttachmentGrid } from "./AttachmentGrid";
import { MediaLightbox } from "./MediaLightbox";
import { MessageMetrics } from "./MessageMetrics";
import {
  normalizeAttachments,
  normalizeMetrics,
} from "../../utils/commsAttachments";

/**
 * Everything a chat message carries beyond its text: reported metrics, media
 * attachments, food items, and the full-screen viewer they open into.
 *
 * Rendered by MessageBubble, so it lights up the coach workspace, the support
 * console, oversight and the client dock at once — they all share Thread.
 *
 * @param {Object} props
 * @param {Object} props.message - Raw server (or optimistic) message.
 */
export const MessageMedia = ({ message }) => {
  const [openIndex, setOpenIndex] = useState(null);

  const attachments = useMemo(
    () => normalizeAttachments(message),
    [message]
  );
  const metrics = useMemo(() => normalizeMetrics(message), [message]);

  /** Metrics point at attachments by index; only offer proof when it resolves. */
  const showProof = useCallback(
    (attachmentIndex) => {
      if (attachments.some((a) => a.index === attachmentIndex)) {
        setOpenIndex(attachmentIndex);
      }
    },
    [attachments]
  );

  if (!attachments.length && !metrics.length) return null;

  return (
    <>
      <MessageMetrics
        metrics={metrics}
        onShowProof={attachments.length ? showProof : undefined}
      />
      <AttachmentGrid attachments={attachments} onOpen={setOpenIndex} />
      {openIndex !== null && (
        <MediaLightbox
          items={attachments}
          index={openIndex}
          onIndexChange={setOpenIndex}
          onClose={() => setOpenIndex(null)}
        />
      )}
    </>
  );
};

export default MessageMedia;
